import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { ArrowRightIcon, CardIcon, DocumentIcon, DownloadIcon, UserIcon } from '../components/icons'

const quickLinks = [
  {
    to: '/downloads',
    label: 'Downloads',
    description: 'Templates, builds and licenses delivered to your workspace.',
    icon: DownloadIcon,
  },
  {
    to: '/agreements',
    label: 'Agreements',
    description: 'Your legal documents and their current versions.',
    icon: DocumentIcon,
  },
  {
    to: '/billing',
    label: 'Billing',
    description: 'Plans, invoices and payment history.',
    icon: CardIcon,
  },
  {
    to: '/account',
    label: 'Account',
    description: 'Profile, organization membership and security.',
    icon: UserIcon,
  },
]

export function HomePage() {
  const { user, memberships } = useAuth()
  const activeOrg = memberships[0]?.organizations

  return (
    <div className="flex flex-col gap-10">
      <header className="flex flex-col gap-3">
        <p className="text-small font-medium uppercase tracking-widest text-cobalt">
          {activeOrg ? activeOrg.name : 'Your workspace'}
        </p>
        <h1 className="font-display text-display-l text-ink">
          Welcome back{user?.email ? ',' : ''}
          {user?.email && <span className="block text-ink-muted">{user.email}</span>}
        </h1>
        <p className="max-w-prose text-body-l text-ink-muted">
          This is your nMach client portal. Access deliverables, review agreements and manage your
          account — all in one place.
        </p>
      </header>

      <section aria-labelledby="quick-links-heading" className="flex flex-col gap-5">
        <h2 id="quick-links-heading" className="font-display text-display-m text-ink">
          Go to
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {quickLinks.map(({ to, label, description, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="group flex items-start gap-4 rounded-2xl border border-line bg-ground-2 p-6 transition-colors duration-micro ease-porcelain hover:border-cobalt/40 hover:bg-ground-3"
            >
              <span className="mt-0.5 text-cobalt">
                <Icon />
              </span>
              <span className="flex flex-1 flex-col gap-1">
                <span className="flex items-center gap-2 text-body font-medium text-ink">
                  {label}
                  <ArrowRightIcon
                    width={16}
                    height={16}
                    className="text-ink-faint transition-transform duration-micro ease-porcelain group-hover:translate-x-1 group-hover:text-cobalt"
                  />
                </span>
                <span className="text-small text-ink-muted">{description}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section aria-labelledby="membership-heading" className="flex flex-col gap-4">
        <h2 id="membership-heading" className="font-display text-display-m text-ink">
          Your memberships
        </h2>
        <ul className="flex flex-col gap-3">
          {memberships.map((membership) => (
            <li
              key={membership.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-ground-2 px-6 py-4"
            >
              <div>
                <p className="text-body font-medium text-ink">
                  {membership.organizations?.name ?? 'Unknown organization'}
                </p>
                <p className="text-small text-ink-muted">
                  {membership.organizations?.kind === 'internal' ? 'nMach internal' : 'Client organization'}
                </p>
              </div>
              <span className="rounded-full border border-line bg-ground-3 px-3 py-1 text-small capitalize text-ink-muted">
                {membership.role} · {membership.status}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
